import { Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbilityService, rxResourceFixed } from '@zayan-erp/shared';
import { FormsModule } from '@angular/forms';
import { of } from 'rxjs';
import { SvgIconsComponent } from '@zayan-erp/shared';
import { SalesService } from '../../../../services/sales.service';
import { CoBuyer } from '../../../../models/co-buyer.models';

@Component({
  selector: 'available-co-buyers-sidebar',
  imports: [FormsModule, SvgIconsComponent],
  templateUrl: './available-co-buyers-sidebar.component.html'
})
export class AvailableCoBuyersSidebarComponent {
  readonly #salesService = inject(SalesService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #abilityService = inject(AbilityService);

  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));

  // Inputs
  readonly prospectId = input.required<string | null>();
  readonly lotId = input.required<string | null>();
  readonly linkedCoBuyerIds = input.required<Set<string>>();
  readonly isActive = input<boolean>(false);
  readonly dealId = input.required<string>();

  // Outputs
  readonly coBuyerLinked = output<void>();

  // Local state
  readonly searchQuery = signal('');

  // Data fetching
  readonly coBuyersResource = rxResourceFixed<CoBuyer[], { prospectId: string | null; reload: number }>({
    params: () => ({ prospectId: this.prospectId(), reload: this.#salesService.refreshTrigger() }),
    stream: ({ params }) =>
      this.prospectId() && params.prospectId ? this.#salesService.readCoBuyers(params.prospectId, false) : of([])
  });

  // Computed filtered list
  readonly filteredCoBuyers = computed(() => {
    const raw = this.coBuyersResource.value();
    const allCoBuyers = Array.isArray(raw) ? raw : [];
    const linkedIds = this.linkedCoBuyerIds();
    const query = this.searchQuery().toLowerCase().trim();

    // Filter out linked co-buyers
    const available = allCoBuyers.filter(coBuyer => coBuyer.id && !linkedIds.has(coBuyer.id));

    // Apply search filter
    if (!query) return available;

    return available.filter(coBuyer => {
      const searchableText = [coBuyer.title, coBuyer.firstName, coBuyer.lastName, coBuyer.email]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(query);
    });
  });

  onLinkCoBuyer(coBuyer: CoBuyer): void {
    if (!this.canWriteDeals()) return;

    const { id: coBuyerId } = coBuyer;

    if (!coBuyerId) return;

    this.#salesService
      .linkCoBuyerToLot(coBuyerId, this.dealId())
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.coBuyerLinked.emit());
  }

  getFullName(coBuyer: CoBuyer): string {
    return [coBuyer.title, coBuyer.firstName, coBuyer.lastName].filter(Boolean).join(' ') || 'N/A';
  }
}
